/**
 * @file rapider-route-reuse-strategy.ts
 * @description Custom Angular RouteReuseStrategy that reuses component instances when
 * navigating between sibling or detail routes sharing the same component (such as
 * `/knowledge/notebooks` overview and `/knowledge/notebooks/:pageKey`).
 * This prevents unnecessary component destruction, scroll resets, and state wipes.
 */
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy } from '@angular/router';

/**
 * Route reuse strategy for Rapider.
 * Reuses component instances when both routes use the identical component class,
 * avoiding component destruction when navigating between overview and document routes.
 */
export class RapiderRouteReuseStrategy extends BaseRouteReuseStrategy {
  /**
   * Determines whether a route should be reused.
   *
   * @param future The route being navigated to.
   * @param curr The current route being navigated from.
   * @returns True if the route component should be reused.
   */
  override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (future.routeConfig === curr.routeConfig) {
      return true;
    }
    return Boolean(
      future.component &&
      curr.component &&
      future.component === curr.component,
    );
  }
}
